import React, { useState, useEffect } from "react";
import { Form, Button, Modal, InputGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import ObjectFieldSelector from "../fieldselector/ObjectFieldSelector";
import PicklistFieldSelector from "./PicklistFieldSelector";
import ObjectConfigApi from "../../api/ObjectConfigApi";

export default function WorkflowFieldUpdate({
  config,
  onChange,
  objectName,
  readOnly,
}) {
  const [showValueFieldSelector, setShowValueFieldSelector] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [availableFields, setAvailableFields] = useState([]);

  // Fetch all fields when objectName changes
  useEffect(() => {
    const fetchFields = async () => {
      if (!objectName) {
        setAvailableFields([]);
        setSelectedField(null);
        return;
      }

      try {
        const response = await ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName);
        if (response.success && response.data?.fields) {
          // Filter out readonly, calculated, and rollupsummary fields
          const filteredFields = response.data.fields.filter(f => {
            const dataType = (f.data_type || '').toLowerCase();
            return !f.is_readonly &&
                   dataType !== 'calculated' &&
                   dataType !== 'rollupsummary';
          });
          setAvailableFields(filteredFields);

          // If fieldName is already set, find and set the selected field
          if (config?.fieldName) {
            const field = filteredFields.find(f => f.field_name === config.fieldName);
            setSelectedField(field || null);
          }
        }
      } catch (error) {
        console.error("Error fetching fields:", error);
        setAvailableFields([]);
        setSelectedField(null);
      }
    };

    fetchFields();
  }, [objectName]);

  // Update selected field when fieldName changes
  useEffect(() => {
    if (config?.fieldName && availableFields.length > 0) {
      const field = availableFields.find(f => f.field_name === config.fieldName);
      setSelectedField(field || null);
    } else if (!config?.fieldName) {
      setSelectedField(null);
    }
  }, [config?.fieldName, availableFields]);

  // Handle field selection from dropdown
  const handleFieldChange = (e) => {
    const fieldName = e.target.value;
    onChange("fieldName", fieldName);
    // Clear newValue when field changes
    onChange("newValue", "");
  };

  // Handle field selection for new value (field reference)
  const handleValueFieldSelect = (field) => {
    onChange("newValue", `{{${field.field_name}}}`);
    setShowValueFieldSelector(false);
  };

  // Extract field name from merge syntax like "{{fieldname}}"
  const getSelectedValueFieldName = () => {
    if (config?.newValue && typeof config.newValue === 'string' && config.newValue.includes('{{')) {
      const match = config.newValue.match(/\{\{(.+?)\}\}/);
      return match ? match[1].trim() : null;
    }
    return null;
  };

  // Check if selected field is a picklist
  const isPicklistField = selectedField && (
    selectedField.data_type === 'picklist' ||
    selectedField.data_type === 'multipicklist'
  );
  const isMultiPicklist = selectedField?.data_type === 'multipicklist';

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Field Name</Form.Label>
        <Form.Select
          value={config?.fieldName || ""}
          onChange={handleFieldChange}
          required={!readOnly}
          disabled={readOnly}
          style={{ fontSize: '0.95rem', padding: '0.5rem 0.75rem', minHeight: '38px' }}
        >
          <option value="">-Select Field-</option>
          {availableFields.map((field) => (
            <option key={field.field_name} value={field.field_name}>
              {field.field_label || field.field_name}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          New Value
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                {isPicklistField
                  ? "Select picklist value(s) from the dropdown"
                  : "Enter a value or select a field. When selecting a field, it will use " +
                    "&#123;&#123;fieldname&#125;&#125; syntax automatically."
                }
              </Tooltip>
            }
          >
            <i className="fa fa-info-circle mx-2 text-muted"></i>
          </OverlayTrigger>
        </Form.Label>

        {isPicklistField ? (
          // Show picklist selector for picklist fields
          <PicklistFieldSelector
            field={selectedField}
            value={config?.newValue || ""}
            onChange={(value) => onChange("newValue", value)}
            disabled={readOnly}
            isMultiSelect={isMultiPicklist}
          />
        ) : (
          // Show text input with field reference option for non-picklist fields
          <>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Enter value or use {{fieldname}} to reference a field"
                value={config?.newValue || ""}
                onChange={(e) => onChange("newValue", e.target.value)}
                disabled={readOnly}
              />
              {!readOnly && (
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowValueFieldSelector(true)}
                  disabled={readOnly}
                >
                  <i className="fa fa-search me-1"></i> Select Field
                </Button>
              )}
            </InputGroup>
            <Form.Text className="text-muted">
              Type a hard value or select a field to use its value
            </Form.Text>
          </>
        )}
      </Form.Group>

      {/* Modal for selecting field for new value */}
      <Modal
        show={showValueFieldSelector}
        onHide={() => setShowValueFieldSelector(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Select Field for Value</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ObjectFieldSelector
            objectName={objectName}
            multiSelect={false}
            onSelect={handleValueFieldSelect}
            excludeReadOnly={true}
            defaultSelectedFieldName={getSelectedValueFieldName()}
          />
        </Modal.Body>
      </Modal>
    </>
  );
}
